import React, { FC, useContext, createContext, useReducer } from 'react';
import { TableRow } from '@code/innovance-react-ui-kit';

export enum ACTIONS {
  SEARCH_CUSTOMER,
  SET_CARD_TYPE,
  CLEAR,
}

type Action = { type: ACTIONS; payload?: any };

interface CustomerSearchValues {
  customerSurname: string;
  customerName: string;
  customerSecondName: string;
  birthDate: Date | null;
  selectedCustomer: TableRow;
  selectedCard: TableRow;

  identityDocumentSerialCode: string;
  identityDocumentSerialNumber: string;
}

interface OnlinePosState {
  customerId?: string;
  customerNumber?: string;
  customerSearchValues?: CustomerSearchValues;
  customerPageNumber?: number;
  cardPageNumber?: number;
}

type OnlinePosDispatch = (action: Action) => void;

const OnlinePosStateContext = createContext<OnlinePosState | undefined>(undefined);
const OnlinePosDispatchContext = createContext<OnlinePosDispatch | undefined>(undefined);

const initialValues = {};

const onlinePosReducer = (state: OnlinePosState, action: Action): OnlinePosState => {
  switch (action.type) {
    case ACTIONS.SEARCH_CUSTOMER:
      return {
        ...state,
        ...action.payload,
      };
    case ACTIONS.CLEAR:
      return initialValues;
    default:
      return state;
  }
};

const OnlinePosProvider: FC = ({ children }) => {
  const [state, dispatch] = useReducer(onlinePosReducer, initialValues);

  return (
    <OnlinePosStateContext.Provider value={state}>
      <OnlinePosDispatchContext.Provider value={dispatch}>
        {children}
      </OnlinePosDispatchContext.Provider>
    </OnlinePosStateContext.Provider>
  );
};

export const useCustomerSearchState = (): OnlinePosState => {
  const context = useContext(OnlinePosStateContext);
  if (context === undefined) {
    throw new Error('useCustomerSearchState must be used within a OnlinePosProvider');
  }

  return context;
};

type SearchInput = Pick<
  OnlinePosState,
  'customerId' | 'customerNumber' | 'customerSearchValues' | 'cardPageNumber' | 'customerPageNumber'
>;

export const useCustomerSearchDispatch = (): ((payload: SearchInput) => void) => {
  const dispatch = useContext(OnlinePosDispatchContext);
  if (dispatch === undefined) {
    throw new Error('useCustomerSearchDispatch must be used within a OnlinePosProvider');
  }

  return (payload: SearchInput) => dispatch({ type: ACTIONS.SEARCH_CUSTOMER, payload });
};

export const useClearDispatch = (): (() => void) => {
  const dispatch = useContext(OnlinePosDispatchContext);
  if (dispatch === undefined) {
    throw new Error('useClearDispatch must be used within a OnlinePosProvider');
  }

  return () => dispatch({ type: ACTIONS.CLEAR });
};

export default OnlinePosProvider;
